import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../../components/Layout';
import { MessageSquare, Send, Search, Filter, Trash2, ChevronLeft, ChevronRight, Bell, X, Inbox, Send as SendIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../api';
import { Message, Conversation, UserProfile } from '../../types/message';
import { useAuth } from '../../contexts/AuthContext';

const ProfessorMessageList: React.FC = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [currentConversationMessages, setCurrentConversationMessages] = useState<Message[]>([]);
  const [newMessageContent, setNewMessageContent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'unread'
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [students, setStudents] = useState<UserProfile[]>([]); // For new message recipient selection
  const [newMessageRecipient, setNewMessageRecipient] = useState<string>('');
  const [newMessageSubject, setNewMessageSubject] = useState('');
  const [newMessageSendType, setNewMessageSendType] = useState<'session' | 'email'>('session');
  const [activeTab, setActiveTab] = useState<'conversations' | 'inbox' | 'sent'>('conversations');
  const [inboxMessages, setInboxMessages] = useState<Message[]>([]);
  const [sentMessages, setSentMessages] = useState<Message[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await api.get<{ unreadCount: number }>('/messages/unread/count');
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<{ conversations: Conversation[] }>('/messages/conversations');
      setConversations(response.data.conversations);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast.error('Erreur lors du chargement des conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInboxMessages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<{ messages: Message[] }>('/messages/inbox');
      setInboxMessages(response.data.messages);
    } catch (error) {
      console.error('Error fetching inbox messages:', error);
      toast.error('Erreur lors du chargement des messages reçus');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSentMessages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<{ messages: Message[] }>('/messages/sent');
      setSentMessages(response.data.messages);
    } catch (error) {
      console.error('Error fetching sent messages:', error);
      toast.error('Erreur lors du chargement des messages envoyés');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchConversationMessages = useCallback(async (conversationId: string) => {
    try {
      const response = await api.get<{ messages: Message[] }>(`/messages/conversation/${conversationId}`);
      setCurrentConversationMessages(response.data.messages);
      fetchUnreadCount(); // After fetching and marking as read, refresh unread count
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
      toast.error('Erreur lors du chargement des messages de la conversation');
    }
  }, [fetchUnreadCount]);

  const fetchStudents = useCallback(async () => {
    try {
      const response = await api.get<{ users: UserProfile[] }>('/users?role=student'); // Assuming /users endpoint exists and returns UserProfile[]
      setStudents(response.data.users);
    } catch (error) {
      console.error('Error fetching students for new message:', error);
      toast.error('Erreur lors du chargement des étudiants');
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    fetchStudents();

    if (activeTab === 'conversations') {
      fetchConversations();
    } else if (activeTab === 'inbox') {
      fetchInboxMessages();
    } else if (activeTab === 'sent') {
      fetchSentMessages();
    }

    const pollingInterval = setInterval(() => {
      fetchUnreadCount();
      if (selectedConversation) {
        fetchConversationMessages(selectedConversation._id);
      }
      if (activeTab === 'inbox') {
        fetchInboxMessages();
      }
      if (activeTab === 'sent') {
        fetchSentMessages();
      }
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(pollingInterval);
  }, [fetchUnreadCount, fetchStudents, selectedConversation, fetchConversationMessages, activeTab, fetchConversations, fetchInboxMessages, fetchSentMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [currentConversationMessages]);

  const handleConversationSelect = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    fetchConversationMessages(conversation._id);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessageContent.trim() || !selectedConversation) return;

    try {
      const response = await api.post('/messages/send', {
        receiverId: selectedConversation.otherParticipant._id,
        conversationId: selectedConversation._id,
        content: newMessageContent,
        subject: selectedConversation.lastMessage?.content ? `Re: ${selectedConversation.lastMessage.content.substring(0, 20)}...` : 'Réponse', // Use existing subject or default
        type: 'session', // Always session for replies
      });
      setNewMessageContent('');
      // Optimistically add the new message to the UI
      if (user) {
        const sentMessage: Message = {
          _id: response.data.data._id, // Assuming backend returns the new message with _id
          sender: { _id: user.id, name: user.name, avatar: user.avatar || '' },
          receiver: selectedConversation.otherParticipant,
          conversationId: selectedConversation._id,
          content: newMessageContent,
          subject: selectedConversation.lastMessage?.content ? `Re: ${selectedConversation.lastMessage.content.substring(0, 20)}...` : 'Réponse',
          read: true, // Sender has read their own message
          type: 'session',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setCurrentConversationMessages(prev => [...prev, sentMessage]);
      }
      fetchConversations(); // Refresh conversations list to update last message/unread count
      toast.success('Message envoyé !');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi du message');
    }
  };

  const handleNewMessageSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessageRecipient || !newMessageSubject || !newMessageContent.trim()) {
      toast.error('Veuillez remplir tous les champs requis.');
      return;
    }

    try {
      await api.post('/messages/send', {
        receiverId: newMessageRecipient,
        subject: newMessageSubject,
        content: newMessageContent,
        type: newMessageSendType,
      });
      toast.success('Nouveau message envoyé !');
      setShowNewMessageModal(false);
      setNewMessageRecipient('');
      setNewMessageSubject('');
      setNewMessageContent('');
      setNewMessageSendType('session');
      fetchConversations();
      fetchUnreadCount();
      // After sending a new message, switch to sent tab or refresh it
      setActiveTab('sent');
      fetchSentMessages();
    } catch (error) {
      console.error('Error sending new message:', error);
      toast.error('Erreur lors de l\'envoi du nouveau message');
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
      try {
        await api.delete(`/messages/${messageId}`);
        toast.success('Message supprimé.');
        // Refresh current view based on active tab
        if (activeTab === 'conversations' && selectedConversation) {
          fetchConversationMessages(selectedConversation._id);
        } else if (activeTab === 'inbox') {
          fetchInboxMessages();
        } else if (activeTab === 'sent') {
          fetchSentMessages();
        }
        fetchConversations(); // Always refresh conversations to update counts
      } catch (error) {
        console.error('Error deleting message:', error);
        toast.error('Erreur lors de la suppression du message');
      }
    }
  };

  const renderMessageList = (messagesToRender: Message[], isInbox: boolean) => {
    if (messagesToRender.length === 0) {
      return (
        <div className="p-4 text-center text-gray-500">
          {isInbox ? 'Aucun message reçu.' : 'Aucun message envoyé.'}
        </div>
      );
    }
    return (
      <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
        {messagesToRender.map((message) => (
          <div
            key={message._id}
            onClick={() => {
              // For inbox messages, mark as read when clicked
              if (isInbox && !message.read) {
                api.put(`/messages/${message._id}/read`).then(() => fetchUnreadCount()).catch(console.error);
              }
              // For simplicity, we'll just show the message content directly for inbox/sent views
              // A more complex implementation might open a conversation view or a message detail modal
              setSelectedConversation(null); // Clear selected conversation to show message detail
              setCurrentConversationMessages([message]); // Display only this message
            }}
            className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
              !message.read && isInbox ? 'bg-brand-50' : ''
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <h3 className={`text-base font-medium ${!message.read && isInbox ? 'text-gray-900 font-bold' : 'text-gray-700'}`}>
                {isInbox ? `De: ${message.sender.name}` : `À: ${message.receiver.name}`}
              </h3>
              <span className="text-xs text-gray-400">
                {new Date(message.createdAt).toLocaleString('fr-FR')}
              </span>
            </div>
            <p className="text-sm text-gray-500 line-clamp-1">
              {message.subject ? `${message.subject} - ` : ''}{message.content}
            </p>
          </div>
        ))}
      </div>
    );
  };

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = searchTerm === '' || 
                          conv.otherParticipant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          conv.lastMessage?.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
                          (filterStatus === 'unread' && conv.unreadCount > 0);

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return <Layout><div className="text-center py-8">Chargement des messages...</div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6 h-full flex flex-col">
        <div className="flex justify-between items-center p-4 bg-white shadow-sm rounded-lg">
          <h1 className="text-3xl font-bold text-gray-900">Messagerie</h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowNewMessageModal(true)}
              className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              <SendIcon className="h-4 w-4 mr-2" />
              Nouveau message
            </button>
            <div className="relative">
              <Bell className="h-6 w-6 text-gray-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-brand-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel: Tabs and List */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <div className="flex space-x-2 mb-4">
                <button
                  onClick={() => setActiveTab('conversations')}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium ${
                    activeTab === 'conversations' ? 'bg-brand-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <MessageSquare className="inline-block w-4 h-4 mr-2" /> Conversations
                </button>
                <button
                  onClick={() => setActiveTab('inbox')}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium ${
                    activeTab === 'inbox' ? 'bg-brand-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Inbox className="inline-block w-4 h-4 mr-2" /> Boîte de réception
                </button>
                <button
                  onClick={() => setActiveTab('sent')}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium ${
                    activeTab === 'sent' ? 'bg-brand-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <SendIcon className="inline-block w-4 h-4 mr-2" /> Envoyés
                </button>
              </div>
              <div className="flex space-x-2">
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-brand-500 focus:border-brand-500"
                />
                {activeTab === 'conversations' && (
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="all">Toutes</option>
                    <option value="unread">Non lues</option>
                  </select>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {activeTab === 'conversations' && (
                filteredConversations.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">Aucune conversation trouvée.</div>
                ) : (
                  filteredConversations.map((conversation) => (
                    <div
                      key={conversation._id}
                      onClick={() => handleConversationSelect(conversation)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedConversation?._id === conversation._id ? 'bg-brand-50 border-l-4 border-brand-500' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <h3 className={`text-base font-medium ${conversation.unreadCount > 0 ? 'text-gray-900 font-bold' : 'text-gray-700'}`}>
                          {conversation.otherParticipant.name}
                        </h3>
                        {conversation.unreadCount > 0 && (
                          <span className="bg-brand-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                            {conversation.unreadCount}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 line-clamp-1">
                        {conversation.lastMessage?.content || 'Pas de message.'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {conversation.lastMessage?.createdAt ? new Date(conversation.lastMessage.createdAt).toLocaleString('fr-FR') : ''}
                      </p>
                    </div>
                  ))
                )
              )}
              {activeTab === 'inbox' && renderMessageList(inboxMessages, true)}
              {activeTab === 'sent' && renderMessageList(sentMessages, false)}
            </div>
          </div>

          {/* Message Content / Chat View */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
            {selectedConversation ? (
              <>
                <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Conversation avec {selectedConversation.otherParticipant.name}
                  </h2>
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-200"
                    title="Retour aux conversations"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </div>
                <div className="flex-1 p-4 overflow-y-auto space-y-4 bg-gray-100">
                  {currentConversationMessages.length === 0 ? (
                    <div className="text-center text-gray-500">Aucun message dans cette conversation.</div>
                  ) : (
                    currentConversationMessages.map((message) => (
                      <div
                        key={message._id}
                        className={`flex ${message.sender._id === user?.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg shadow-md ${
                            message.sender._id === user?.id
                              ? 'bg-brand-600 text-white'
                              : 'bg-gray-200 text-gray-800'
                          }`}
                        >
                          <p className="font-semibold text-sm">{message.sender.name}</p>
                          <p className="text-sm mt-1">{message.content}</p>
                          <p className="text-xs opacity-80 mt-1 text-right">
                            {new Date(message.createdAt).toLocaleString('fr-FR')}
                          </p>
                          {message.sender._id === user?.id && (
                            <button 
                              onClick={() => handleDeleteMessage(message._id)}
                              className="ml-2 text-xs text-brand-200 hover:text-brand-400"
                              title="Supprimer le message"
                            >
                              <Trash2 className="h-3 w-3 inline" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>
                <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white flex items-center space-x-2">
                  <textarea
                    value={newMessageContent}
                    onChange={(e) => setNewMessageContent(e.target.value)}
                    rows={1}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
                    placeholder="Écrivez votre message..."
                  />
                  <button
                    type="submit"
                    disabled={!newMessageContent.trim()}
                    className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <SendIcon className="h-5 w-5" />
                  </button>
                </form>
              </>
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center p-4">
                  <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Sélectionnez une conversation</h3>
                  <p className="text-gray-500">
                    Choisissez une conversation dans la liste pour la consulter et y répondre.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* New Message Modal */}
        {showNewMessageModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full shadow-xl">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Nouveau message</h2>
                <button
                  onClick={() => setShowNewMessageModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleNewMessageSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Destinataire
                  </label>
                  <select
                    value={newMessageRecipient}
                    onChange={(e) => setNewMessageRecipient(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    required
                  >
                    <option value="">Sélectionner un utilisateur</option>
                    {users.map(u => (
                      <option key={u._id} value={u._id}>{u.name} ({u.email})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sujet
                  </label>
                  <input
                    type="text"
                    value={newMessageSubject}
                    onChange={(e) => setNewMessageSubject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="Sujet du message"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contenu
                  </label>
                  <textarea
                    rows={6}
                    value={newMessageContent}
                    onChange={(e) => setNewMessageContent(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="Contenu du message..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type d'envoi
                  </label>
                  <select
                    value={newMessageSendType}
                    onChange={(e) => setNewMessageSendType(e.target.value as 'session' | 'email')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="session">Message interne (session)</option>
                    <option value="email">Envoyer aussi par Email</option>
                  </select>
                </div>

                <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowNewMessageModal(false)}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
                  >
                    Envoyer
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProfessorMessageList;